import React, { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import { FaChevronLeft, FaChevronRight, FaPlus } from 'react-icons/fa';
import { priceOnFinish } from '../../supplementary_components/priceInputDelegate';
import { API_ROOT, sendRequest } from '../../supplementary_components/requests';
import { addNotification, addPopup } from '../../supplementary_components/store';
import { Form } from '../../general_components/forms/Form';
import {
    WeekSelector,
    dateToLocalDateString,
} from './weeklyReports';
import styles from './reports.module.scss';

const PERIOD_COUNT = 3;
const TIMEFRAME = 'monthly';

const ADJUSTMENT_SECTIONS = {
    OPERATIONAL_REVENUE: 'OPERATIONAL_REVENUE',
    VARIABLE_COSTS: 'VARIABLE_COSTS',
    FIXED_COSTS: 'FIXED_COSTS',
};

function currentCompletedMonthStart() {
    const now = new Date();
    return new Date(now.getFullYear(), now.getMonth() - 1, 1);
}

function shiftMonthStart(start, direction) {
    const d = new Date(start);
    d.setMonth(d.getMonth() + direction);
    return d;
}

function endOfMonth(start) {
    const s = new Date(start);
    const e = new Date(s);
    e.setMonth(s.getMonth() + 1);
    e.setDate(0);
    return e;
}

function monthCacheKey(start) {
    return `${TIMEFRAME}|${dateToLocalDateString(start)}`;
}

function normalizeReportResponse(res) {
    if (!res || typeof res !== 'object') {
        return { periods: [], adjustmentLines: {} };
    }
    const periods = Array.isArray(res.periods) ? res.periods : [];
    const adjustmentLines = res.adjustmentLines && typeof res.adjustmentLines === 'object'
        ? res.adjustmentLines
        : {};
    return { periods, adjustmentLines };
}

function isPeriodNoData(period) {
    return Boolean(period && typeof period === 'object' && period.noData === true);
}

function monthlyCompanyReportUrl(startDate, endDate) {
    const qs = new URLSearchParams({ startDate, endDate }).toString();
    return `${API_ROOT}reports/monthly-company?${qs}`;
}

const fmtMoney = (v) => (v == null ? '—' : priceOnFinish(v, false, true));
const fmtMiles = (v) => (v == null ? '—' : (Number(v) || 0).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 }));
const fmtRate = (v) => (v == null ? '—' : priceOnFinish(v, false, true));
const fmtCount1 = (v) => (v == null ? '—' : (Number(v) || 0).toFixed(2));
const fmtWeeks = (v) => (v == null ? '—' : (Number(v) || 0).toFixed(0));

function divOrNull(numerator, denominator) {
    const d = Number(denominator) || 0;
    if (d <= 0) return null;
    return (Number(numerator) || 0) / d;
}

/** Earnings Before Taxes / Gross Profit, as a percentage (can be negative). */
function computeEbtToGrossProfitPct(d) {
    const grossProfit = Number(d.grossProfit) || 0;
    const ebt = Number(d.earningsBeforeTaxes);
    if (!Number.isFinite(ebt) || grossProfit === 0) return null;
    return (ebt / grossProfit) * 100;
}

function computeDerivedMetrics(d) {
    const miles = Number(d.totalMiles) || 0;
    const activeTrucks = Number(d.averageActiveTrucks) || 0;
    const weeks = Number(d.weekCount) || 0;
    const salary = (Number(d.salaryCompany) || 0) + (Number(d.salaryLeaseOwn) || 0);
    const fuel = (Number(d.fuelCardCompany) || 0)
        + (Number(d.fuelCardLeaseOwn) || 0)
        + (Number(d.fuelCard) || 0)
        + (Number(d.efsFuelCompany) || 0)
        + (Number(d.efsFuelLeaseOwn) || 0)
        + (Number(d.efsFuel) || 0);
    const maintenance = (Number(d.maintenanceTruck) || 0) + (Number(d.maintenanceTrailer) || 0);
    const varTotal = Number(d.totalVariableCosts) || 0;
    const fixedTotal = Number(d.totalFixedCosts) || 0;
    const revenue = Number(d.operationalRevenue) || 0;

    const salaryPerMile = divOrNull(salary, miles);
    const fuelPerMile = divOrNull(fuel, miles);
    const maintenancePerMile = divOrNull(maintenance, miles);
    const variableCostPerMile = divOrNull(varTotal, miles);
    const fixedCostPerMile = divOrNull(fixedTotal, miles);
    const breakEvenPerMile = variableCostPerMile != null && fixedCostPerMile != null
        ? variableCostPerMile + fixedCostPerMile
        : null;
    const revenuePerTruckPerMile = divOrNull(revenue, miles);
    const profitPerMile = revenuePerTruckPerMile != null && breakEvenPerMile != null
        ? revenuePerTruckPerMile - breakEvenPerMile
        : null;

    const weeklyPerTruck = (amount) => divOrNull(divOrNull(amount, activeTrucks), weeks);
    const variableCostPerTruck = weeklyPerTruck(varTotal);
    const fixedCostPerTruck = weeklyPerTruck(fixedTotal);
    const breakEvenPerTruck = variableCostPerTruck != null && fixedCostPerTruck != null
        ? variableCostPerTruck + fixedCostPerTruck
        : null;
    const revenuePerTruck = weeklyPerTruck(revenue);
    const profitPerTruck = revenuePerTruck != null && breakEvenPerTruck != null
        ? revenuePerTruck - breakEvenPerTruck
        : null;

    return {
        salaryPerMile,
        fuelPerMile,
        maintenancePerMile,
        variableCostPerMile,
        fixedCostPerMile,
        breakEvenPerMile,
        revenuePerTruckPerMile,
        profitPerMile,
        variableCostPerTruck,
        fixedCostPerTruck,
        breakEvenPerTruck,
        revenuePerTruck,
        profitPerTruck,
    };
}

function adjustmentLineAddFormConfig(section) {
    return {
        formAccessLevel: { key: 'MONTHLY_REPORTS', level: 'MANAGE' },
        formTitle: 'Add Report Row',
        name: 'addMonthlyReportAdjustmentLine',
        addLink: `${API_ROOT}monthly-report-adjustments/lines`,
        successMessage: 'Adjustment row added',
        buildRequestBody: (data) => ({
            section,
            label: String(data.label ?? '').trim(),
        }),
        columns: [
            {
                label: 'Label',
                name: 'label',
                type: 'text',
                autoComplete: 'off',
            },
        ],
    };
}

const EQUIPMENT_FIXED_COST_ORDER = [
    'Truck Purchase',
    'Truck Leasing',
    'Trailer Purchase',
    'Trailer Leasing',
];

function collectFixedCostLabels(visiblePeriods, getPeriodData) {
    const seen = [];
    const set = new Set();
    visiblePeriods.forEach((p) => {
        const d = getPeriodData(p);
        if (!d || isPeriodNoData(d) || !Array.isArray(d.fixedCosts)) return;
        d.fixedCosts.forEach((row) => {
            if (row.label && !set.has(row.label)) {
                set.add(row.label);
                seen.push(row.label);
            }
        });
    });
    const equipment = EQUIPMENT_FIXED_COST_ORDER.filter((label) => set.has(label));
    const rest = seen.filter((label) => !EQUIPMENT_FIXED_COST_ORDER.includes(label));
    return [...equipment, ...rest];
}

function fixedCostAmount(period, label) {
    if (!period || !Array.isArray(period.fixedCosts)) return 0;
    const match = period.fixedCosts.find((r) => r.label === label);
    return match ? (Number(match.amount) || 0) : 0;
}

function adjustmentAmountForPeriod(period, lineId, fieldName) {
    if (!period || !Array.isArray(period[fieldName])) return 0;
    const match = period[fieldName].find((r) => r.lineId === lineId);
    return match ? (Number(match.amount) || 0) : 0;
}

const LoadingValue = () => <span className={styles.cellLoading}>…</span>;
const NoDataValue = () => <span className={styles.cellNoData}>No Data</span>;

function amountsEqual(a, b) {
    const na = parseFloat(a);
    const nb = parseFloat(b);
    if (Number.isNaN(na) || Number.isNaN(nb)) return false;
    return Math.abs(na - nb) < 0.005;
}

function parseAmountInput(val) {
    const parsed = parseFloat(String(val ?? '').replace(/[^0-9.-]/g, ''));
    return Number.isNaN(parsed) ? null : parsed;
}

function sanitizeAdjustmentDraft(val) {
    let s = String(val ?? '');
    const negative = s.startsWith('-');
    s = s.replace(/[^0-9.]/g, '');
    const dot = s.indexOf('.');
    if (dot !== -1) {
        s = s.slice(0, dot + 1) + s.slice(dot + 1).replace(/\./g, '');
    }
    if (s === '' || s === '.') {
        return negative ? '-' : '';
    }
    return negative ? `-${s}` : s;
}

function adjustmentDraftFromAmount(amount) {
    const n = Number(amount ?? 0);
    return Number.isFinite(n) ? String(n) : '0';
}

function EbtMarginBadge({ pct }) {
    if (pct == null || !Number.isFinite(pct)) return null;
    const positive = pct > 0;
    return (
        <span className={`${styles.ebtMarginBadge} ${positive ? styles.ebtMarginBadgePos : styles.ebtMarginBadgeNeg}`}>
            {positive ? '+' : ''}{pct.toFixed(2)}%
        </span>
    );
}

function AdjustmentAmountCell({ lineId, period, fieldName, year, month, onSaved }) {
    const [editing, setEditing] = useState(false);
    const [draft, setDraft] = useState('');
    const originalRef = useRef(0);

    const amount = period && !isPeriodNoData(period)
        ? adjustmentAmountForPeriod(period, lineId, fieldName)
        : null;

    useEffect(() => {
        originalRef.current = amount ?? 0;
    }, [amount]);

    if (!period) return <LoadingValue />;
    if (isPeriodNoData(period)) return <NoDataValue />;

    const startEdit = () => {
        originalRef.current = amount ?? 0;
        setDraft(adjustmentDraftFromAmount(amount ?? 0));
        setEditing(true);
    };

    const cancelEdit = () => {
        setEditing(false);
        setDraft('');
    };

    const commitEdit = () => {
        const originalAmount = originalRef.current;
        const parsed = parseAmountInput(draft);
        if (parsed == null || amountsEqual(parsed, originalAmount)) {
            cancelEdit();
            return;
        }
        onSaved(lineId, year, month, parsed);
        cancelEdit();
        sendRequest(
            'PUT',
            `${API_ROOT}monthly-report-adjustments`,
            null,
            () => {},
            () => {
                addNotification({ message: 'Could not save adjustment', type: 'alert' });
                onSaved(lineId, year, month, originalAmount);
            },
            { lineId, year, month, amount: parsed },
        );
    };

    if (editing) {
        return (
            <input
                type="text"
                className={styles.adjustmentInput}
                value={draft}
                autoFocus
                autoComplete="off"
                onChange={(e) => setDraft(sanitizeAdjustmentDraft(e.target.value))}
                onBlur={commitEdit}
                onKeyDown={(e) => {
                    if (e.key === 'Enter') {
                        e.preventDefault();
                        e.currentTarget.blur();
                    }
                    if (e.key === 'Escape') {
                        e.preventDefault();
                        cancelEdit();
                    }
                }}
            />
        );
    }

    return (
        <button type="button" className={styles.adjustmentCellBtn} onClick={startEdit}>
            {fmtMoney(amount)}
        </button>
    );
}

function AddAdjustmentButton({ section, onAdded }) {
    const handleAdd = () => {
        addPopup({
            content: Form,
            popupData: { configuration: adjustmentLineAddFormConfig(section) },
            postSuccessAction: (res) => onAdded(res),
            EZCloseAllowed: false,
        });
    };
    return (
        <button type="button" className={styles.addAdjustmentBtn} onClick={handleAdd} title="Add row">
            <FaPlus />
        </button>
    );
}

export const MonthlyCompanyReport = () => {
    const [anchorStart, setAnchorStart] = useState(() => currentCompletedMonthStart());
    const [dataCache, setDataCache] = useState({});
    const [adjustmentLines, setAdjustmentLines] = useState({
        OPERATIONAL_REVENUE: [],
        VARIABLE_COSTS: [],
        FIXED_COSTS: [],
    });
    const prevAnchorMsRef = useRef(null);

    const visiblePeriods = useMemo(() => {
        const arr = [];
        for (let i = PERIOD_COUNT - 1; i >= 0; i--) {
            const start = shiftMonthStart(anchorStart, -i);
            arr.push({ start, end: endOfMonth(start) });
        }
        return arr;
    }, [anchorStart]);

    const windowRangeLabel = useMemo(() => {
        const first = visiblePeriods[0];
        const last = visiblePeriods[visiblePeriods.length - 1];
        if (!first || !last) return '';
        const sameYear = first.start.getFullYear() === last.end.getFullYear();
        const a = first.start.toLocaleDateString('en-US', { month: 'short' });
        const b = last.end.toLocaleDateString('en-US', { month: 'short' });
        if (sameYear) return `${a} - ${b}`;
        return `${a} '${String(first.start.getFullYear()).slice(2)} - ${b} '${String(last.end.getFullYear()).slice(2)}`;
    }, [visiblePeriods]);

    useEffect(() => {
        let cancelled = false;
        const ac = new AbortController();
        const firstVis = visiblePeriods[0];
        const lastVis = visiblePeriods[visiblePeriods.length - 1];
        if (!firstVis || !lastVis) return undefined;

        const anchorMs = anchorStart.getTime();
        if (prevAnchorMsRef.current === anchorMs) {
            return undefined;
        }

        const startDate = dateToLocalDateString(firstVis.start);
        const endDate = dateToLocalDateString(lastVis.end);
        const keysToPlaceholder = visiblePeriods.map((p) => monthCacheKey(p.start));

        setDataCache((prev) => {
            const next = { ...prev };
            keysToPlaceholder.forEach((k) => {
                if (next[k] === undefined) next[k] = null;
            });
            return next;
        });

        sendRequest(
            'GET',
            monthlyCompanyReportUrl(startDate, endDate),
            ac.signal,
            (res) => {
                if (cancelled) return;
                const { periods, adjustmentLines: lines } = normalizeReportResponse(res);
                setAdjustmentLines({
                    OPERATIONAL_REVENUE: lines.OPERATIONAL_REVENUE || [],
                    VARIABLE_COSTS: lines.VARIABLE_COSTS || [],
                    FIXED_COSTS: lines.FIXED_COSTS || [],
                });
                const byYearMonth = {};
                periods.forEach((period) => {
                    if (period && period.year != null && period.month != null) {
                        byYearMonth[`${period.year}-${period.month}`] = period;
                    }
                });
                setDataCache((prev) => {
                    const next = { ...prev };
                    visiblePeriods.forEach((p) => {
                        const ym = `${p.start.getFullYear()}-${p.start.getMonth() + 1}`;
                        next[monthCacheKey(p.start)] = byYearMonth[ym] ?? { noData: true };
                    });
                    return next;
                });
                prevAnchorMsRef.current = anchorMs;
            },
            () => {
                if (!cancelled) {
                    addNotification({ message: 'Could not load monthly company report.', type: 'alert' });
                    setDataCache((prev) => {
                        const next = { ...prev };
                        keysToPlaceholder.forEach((k) => { delete next[k]; });
                        return next;
                    });
                }
            },
            null,
        );

        return () => {
            cancelled = true;
            ac.abort();
        };
    }, [anchorStart, visiblePeriods]);

    const getPeriodData = useCallback(
        (p) => dataCache[monthCacheKey(p.start)] || null,
        [dataCache],
    );

    const handleAdjustmentSaved = useCallback((lineId, year, month, amount) => {
        setDataCache((prev) => {
            const next = { ...prev };
            Object.keys(next).forEach((key) => {
                const period = next[key];
                if (!period || isPeriodNoData(period)) return;
                if (period.year !== year || period.month !== month) return;
                const patchList = (list, field) => {
                    if (!Array.isArray(period[field])) return;
                    period[field] = period[field].map((row) =>
                        String(row.lineId) === String(lineId) ? { ...row, amount } : row,
                    );
                };
                patchList(period, 'revenueAdjustments');
                patchList(period, 'variableCostAdjustments');
                patchList(period, 'fixedCostAdjustments');
                const revenueAdj = (period.revenueAdjustments || []).reduce((s, r) => s + (Number(r.amount) || 0), 0);
                period.grossProfit = (Number(period.operationalRevenue) || 0) + revenueAdj;
                const varAdj = (period.variableCostAdjustments || []).reduce((s, r) => s + (Number(r.amount) || 0), 0);
                period.totalVariableCosts = (Number(period.salaryCompany) || 0)
                    + (Number(period.salaryLeaseOwn) || 0)
                    + (Number(period.fuelCardCompany) || 0)
                    + (Number(period.fuelCardLeaseOwn) || 0)
                    + (Number(period.fuelCard) || 0)
                    + (Number(period.efsFuelCompany) || 0)
                    + (Number(period.efsFuelLeaseOwn) || 0)
                    + (Number(period.efsFuel) || 0)
                    + (Number(period.tollway) || 0)
                    + (Number(period.maintenanceTruck) || 0)
                    + (Number(period.maintenanceTrailer) || 0)
                    + (Number(period.otherOperationExpenses) || 0)
                    + varAdj;
                const fixedAdj = (period.fixedCostAdjustments || []).reduce((s, r) => s + (Number(r.amount) || 0), 0);
                const fixedBase = (period.fixedCosts || []).reduce((s, r) => s + (Number(r.amount) || 0), 0);
                period.totalFixedCosts = fixedBase + fixedAdj;
                period.earningsBeforeTaxes = period.grossProfit - period.totalVariableCosts - period.totalFixedCosts;
                next[key] = { ...period };
            });
            return next;
        });
    }, []);

    const handleLineAdded = useCallback((lineDto) => {
        if (!lineDto || !lineDto.section) return;
        setAdjustmentLines((prev) => {
            const section = lineDto.section;
            const list = [...(prev[section] || [])];
            if (!list.some((l) => l.id === lineDto.id)) {
                list.push(lineDto);
            }
            return { ...prev, [section]: list };
        });
        setDataCache((prev) => {
            const next = { ...prev };
            const fieldBySection = {
                OPERATIONAL_REVENUE: 'revenueAdjustments',
                VARIABLE_COSTS: 'variableCostAdjustments',
                FIXED_COSTS: 'fixedCostAdjustments',
            };
            const field = fieldBySection[lineDto.section];
            Object.keys(next).forEach((key) => {
                const period = next[key];
                if (!period || isPeriodNoData(period)) return;
                const rows = [...(period[field] || [])];
                if (!rows.some((r) => r.lineId === lineDto.id)) {
                    rows.push({ lineId: lineDto.id, label: lineDto.label, amount: 0 });
                }
                next[key] = { ...period, [field]: rows };
            });
            return next;
        });
    }, []);

    const fixedCostLabels = useMemo(
        () => collectFixedCostLabels(visiblePeriods, getPeriodData),
        [visiblePeriods, getPeriodData],
    );

    const anyDataLoaded = visiblePeriods.some((p) => !!getPeriodData(p));
    const gridStyle = { '--fleet-col-count': PERIOD_COUNT };

    const SectionSpacer = () => <div className={styles.tblSectionSpacer} aria-hidden="true" />;

    const SectionRow = ({ label, subLabel, variant }) => (
        <div className={`${styles.tblSectionRow} ${variant === 'weekly' ? styles.tblSectionRowWeekly : ''}`}>
            <span>{label}</span>
            {subLabel ? <small>{subLabel}</small> : null}
        </div>
    );

    const MetricRow = ({ label, accessor, kind = 'plain', indent = false, cellClass = '' }) => (
        <div className={styles.tblRow}>
            <div className={`${styles.tblLabel} ${kind === 'total' ? styles.tblLabelTotal : ''} ${kind === 'muted' ? styles.tblLabelMuted : ''} ${indent ? styles.tblLabelIndented : ''}`}>
                {label}
            </div>
            {visiblePeriods.map((p, idx) => {
                const d = getPeriodData(p);
                return (
                    <div
                        key={idx}
                        className={`${styles.tblValue} ${kind === 'total' ? styles.tblValueTotal : ''} ${kind === 'muted' ? styles.tblValueMuted : ''} ${cellClass ? styles[cellClass] : ''}`}
                    >
                        {!d ? <LoadingValue /> : isPeriodNoData(d) ? <NoDataValue /> : accessor(d, p)}
                    </div>
                );
            })}
        </div>
    );

    const HeaderRow = () => (
        <div className={`${styles.tblRow} ${styles.tblRowStatic}`}>
            <div className={styles.tblCorner}>
                <div className={styles.tblCornerLabel}>Metric</div>
            </div>
            {visiblePeriods.map((p, idx) => {
                const d = getPeriodData(p);
                return (
                    <div key={idx} className={styles.tblPeriodHead}>
                        <div className={styles.tblPeriodRange}>
                            {d && d.monthLabel ? d.monthLabel : p.start.toLocaleDateString('en-US', { month: 'long', year: 'numeric' })}
                        </div>
                        {d && !isPeriodNoData(d) && (
                            <small className={styles.tblPeriodSub}>
                                {d.weekDateRange} · {fmtWeeks(d.weekCount)} Weeks
                            </small>
                        )}
                    </div>
                );
            })}
        </div>
    );

    const renderAdjustmentRows = (section, fieldName) => (
        <>
            {(adjustmentLines[section] || []).map((line) => (
                <MetricRow
                    key={line.id}
                    label={line.label}
                    kind="muted"
                    accessor={(d) => (
                        <AdjustmentAmountCell
                            lineId={line.id}
                            period={d}
                            fieldName={fieldName}
                            year={d.year}
                            month={d.month}
                            onSaved={handleAdjustmentSaved}
                        />
                    )}
                />
            ))}
            <div className={styles.tblRow}>
                <div className={styles.tblLabel}>
                    <AddAdjustmentButton section={section} onAdded={handleLineAdded} />
                </div>
                {visiblePeriods.map((_, idx) => (
                    <div key={idx} className={styles.tblValue} />
                ))}
            </div>
        </>
    );

    return (
        <div className={styles.mainContainer}>
            <WeekSelector>
                <FaChevronLeft onClick={() => setAnchorStart((prev) => shiftMonthStart(prev, -1))} />
                <span className={`non-selectable ${styles.fleetRangeLabel}`}>{windowRangeLabel}</span>
                <FaChevronRight onClick={() => setAnchorStart((prev) => shiftMonthStart(prev, 1))} />
            </WeekSelector>

            {!anyDataLoaded ? (
                <div className={styles.fleetLoadingState}>Loading…</div>
            ) : (
                <div className={styles.tblWrap}>
                    <div className={styles.tbl} style={gridStyle}>
                        <HeaderRow />

                        <SectionRow label="Fleet" />
                        <MetricRow label="Total Trucks" accessor={(d) => fmtCount1(d.totalTrucks)} />
                        <MetricRow label="Average Active Trucks" accessor={(d) => fmtCount1(d.averageActiveTrucks)} />
                        <MetricRow label="Total Miles" accessor={(d) => fmtMiles(d.totalMiles)} />

                        <SectionSpacer />
                        <MetricRow
                            label="Variable Cost per Mile"
                            kind="total"
                            accessor={(d) => fmtRate(computeDerivedMetrics(d).variableCostPerMile)}
                        />
                        <MetricRow
                            label="Salaries"
                            kind="muted"
                            indent
                            accessor={(d) => fmtRate(computeDerivedMetrics(d).salaryPerMile)}
                        />
                        <MetricRow
                            label="Fuel"
                            kind="muted"
                            indent
                            accessor={(d) => fmtRate(computeDerivedMetrics(d).fuelPerMile)}
                        />
                        <MetricRow
                            label="Maintenance"
                            kind="muted"
                            indent
                            accessor={(d) => fmtRate(computeDerivedMetrics(d).maintenancePerMile)}
                        />
                        <MetricRow
                            label="Fixed Cost per Mile"
                            accessor={(d) => fmtRate(computeDerivedMetrics(d).fixedCostPerMile)}
                        />
                        <MetricRow
                            label="Break-even per Mile"
                            kind="total"
                            cellClass="tblCellBreakEven"
                            accessor={(d) => fmtRate(computeDerivedMetrics(d).breakEvenPerMile)}
                        />
                        <MetricRow
                            label="Revenue Per Truck per Mile"
                            accessor={(d) => fmtRate(computeDerivedMetrics(d).revenuePerTruckPerMile)}
                        />
                        <MetricRow
                            label="Profit per Truck"
                            kind="total"
                            cellClass="tblCellProfit"
                            accessor={(d) => {
                                const v = computeDerivedMetrics(d).profitPerMile;
                                return (
                                    <span className={v != null && v < 0 ? styles.negative : styles.positive}>
                                        {fmtRate(v)}
                                    </span>
                                );
                            }}
                        />
                        <SectionSpacer />
                        <SectionRow label="Weekly Calculation"  />
                        <MetricRow
                            label="Variable Cost per Truck"
                            accessor={(d) => fmtMoney(computeDerivedMetrics(d).variableCostPerTruck)}
                        />
                        <MetricRow
                            label="Fixed Cost per Truck"
                            accessor={(d) => fmtMoney(computeDerivedMetrics(d).fixedCostPerTruck)}
                        />
                        <MetricRow
                            label="Break-even"
                            kind="total"
                            cellClass="tblCellBreakEven"
                            accessor={(d) => fmtMoney(computeDerivedMetrics(d).breakEvenPerTruck)}
                        />
                        <MetricRow
                            label="Revenue per Truck"
                            accessor={(d) => fmtMoney(computeDerivedMetrics(d).revenuePerTruck)}
                        />
                        <MetricRow
                            label="Profit per Truck"
                            kind="total"
                            cellClass="tblCellProfit"
                            accessor={(d) => {
                                const v = computeDerivedMetrics(d).profitPerTruck;
                                return (
                                    <span className={v != null && v < 0 ? styles.negative : styles.positive}>
                                        {fmtMoney(v)}
                                    </span>
                                );
                            }}
                        />
                        <SectionSpacer />
                        <SectionRow label="Operational Revenue" />
                        <MetricRow label="Operational Revenue" kind="muted" accessor={(d) => fmtMoney(d.operationalRevenue)} />
                        {renderAdjustmentRows(ADJUSTMENT_SECTIONS.OPERATIONAL_REVENUE, 'revenueAdjustments')}
                        <MetricRow label="Gross Profit" kind="total" accessor={(d) => fmtMoney(d.grossProfit)} />
                        <SectionSpacer />
                        <SectionRow label="Variable Costs" />
                        <MetricRow label="Company Driver Salaries" kind="muted" accessor={(d) => fmtMoney(d.salaryCompany)} />
                        <MetricRow label="Owner/Lease Salaries" kind="muted" accessor={(d) => fmtMoney(d.salaryLeaseOwn)} />
                        <MetricRow label="Fuel Card - Company" kind="muted" accessor={(d) => fmtMoney(d.fuelCardCompany)} />
                        <MetricRow label="Fuel Card - Lease/Own" kind="muted" accessor={(d) => fmtMoney(d.fuelCardLeaseOwn)} />
                        <MetricRow label="EFS Fuel - Company" kind="muted" accessor={(d) => fmtMoney(d.efsFuelCompany)} />
                        <MetricRow label="EFS Fuel - Lease/Own" kind="muted" accessor={(d) => fmtMoney(d.efsFuelLeaseOwn)} />
                        <MetricRow label="Fuel Card" kind="muted" accessor={(d) => fmtMoney(d.fuelCard)} />
                        <MetricRow label="EFS Fuel" kind="muted" accessor={(d) => fmtMoney(d.efsFuel)} />
                        <MetricRow label="Tollway" kind="muted" accessor={(d) => fmtMoney(d.tollway)} />
                        <MetricRow label="Maintenance — Truck" kind="muted" accessor={(d) => fmtMoney(d.maintenanceTruck)} />
                        <MetricRow label="Maintenance — Trailer" kind="muted" accessor={(d) => fmtMoney(d.maintenanceTrailer)} />
                        <MetricRow label="Other Operation Expenses" kind="muted" accessor={(d) => fmtMoney(d.otherOperationExpenses)} />
                        {renderAdjustmentRows(ADJUSTMENT_SECTIONS.VARIABLE_COSTS, 'variableCostAdjustments')}
                        <MetricRow label="Total Operating Expense" kind="total" accessor={(d) => fmtMoney(d.totalVariableCosts)} />
                        <SectionSpacer />
                        <SectionRow label="Fixed Costs" />
                        {fixedCostLabels.map((label) => (
                            <MetricRow
                                key={label}
                                label={label}
                                kind="muted"
                                accessor={(d) => fmtMoney(fixedCostAmount(d, label))}
                            />
                        ))}
                        {renderAdjustmentRows(ADJUSTMENT_SECTIONS.FIXED_COSTS, 'fixedCostAdjustments')}
                        <MetricRow label="Total Fixed Expenses" kind="total" accessor={(d) => fmtMoney(d.totalFixedCosts)} />
                        <SectionSpacer />
                        {/* <SectionRow label="Bottom Line" /> */}
                        <MetricRow
                            label="Earnings Before Taxes"
                            kind="total"
                            accessor={(d) => (
                                <div className={styles.valueWithBadge}>
                                    <span className={d.earningsBeforeTaxes >= 0 ? styles.positive : styles.negative}>
                                        {fmtMoney(d.earningsBeforeTaxes)}
                                    </span>
                                    <EbtMarginBadge pct={computeEbtToGrossProfitPct(d)} />
                                </div>
                            )}
                        />
                        <SectionSpacer />
                    </div>
                </div>
            )}
        </div>
    );
};
